import React, {memo, useState} from 'react'
import _ from 'lodash'
import {Button, Form, Grid, Header, List, Message, Segment} from 'semantic-ui-react'
import axios from 'axios'
import {withErrorHandling, withHelmet, withQueryParams, withMeData} from '../../hoc'
import {asResponse} from '../../util'

const createAccount = (account) => {
  return asResponse(axios.post('/bff/accounts/create', account))
}

const _CreateAccount = (
  {
    query,
    navigate,
    createAccount,
    basePath = '/c',
    mutateMe
  }
) => {

  const getDefaultAccountName = (email) => {
    if (!_.isEmpty(email) && !_.isNil(email)) {
      return email.split('@')[0]
    }
    return ''
  }

  const [state, setState] = useState({
    invitationId: query.invitationId,
    name: query.accountName ? query.accountName : '',
    first: '',
    last: '',
    email: query.email || '',
    password: '',
    passwordConfirmation: '',
    onSubmitted: false,
    errors: {}
  })

  const isEmailValid = (email) => {
    const regExp = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return regExp.test(email)
  }

  const validate = ({name, email, first, last, password, passwordConfirmation, errors = {}}) => {
    if (!query.invitationId) {
      name = name || getDefaultAccountName(email)
      if (!name || _.isEmpty(name.trim())) {
        errors['registration.name.required'] = 'Account name required'
      } else {
        delete errors['registration.name.required']
        if (name.length > 150) {
          errors['registration.name.exceeds.max.length'] = 'Account name cannot exceed 150 characters'
        } else {
          delete errors['registration.name.exceeds.max.length']
        }
      }
      if (!email || _.isEmpty(email.trim())) {
        errors['registration.email.required'] = 'Email required'
      } else {
        delete errors['registration.email.required']
        if (email.length > 255) {
          errors['registration.email.exceeds.max.length'] = 'Email cannot exceed 255 characters'
        } else {
          delete errors['registration.email.exceeds.max.length']
          if (!isEmailValid(email)) {
            errors['registration.email.invalid.stateat'] = 'Email must be valid'
          } else {
            delete errors['registration.email.invalid.stateat']
          }
        }
      }
    }
    if (!first || _.isEmpty(first.trim())) {
      errors['registration.first.required'] = 'First name required'
    } else {
      delete errors['registration.first.required']
      if (first.length > 150) {
        errors['registration.first.exceeds.max.length'] = 'First name cannot exceed 150 characters'
      } else {
        delete errors['registration.first.exceeds.max.length']
      }
    }
    if (!last || _.isEmpty(last.trim())) {
      errors['registration.last.required'] = 'Last name required'
    } else {
      delete errors['registration.last.required']
      if (last.length > 150) {
        errors['registration.last.exceeds.max.length'] = 'Last name cannot exceed 150 characters'
      } else {
        delete errors['registration.last.exceeds.max.length']
      }
    }
    if (!password || _.isEmpty(password.trim())) {
      errors['registration.password.required'] = 'Password required'
    } else {
      delete errors['registration.password.required']
      if (password.length > 255) {
        errors['registration.password.exceeds.max.length'] = 'Password cannot exceed 255 characters'
      } else {
        delete errors['registration.password.exceeds.max.length']
      }
    }
    if (!passwordConfirmation || _.isEmpty(passwordConfirmation.trim())) {
      errors['registration.passwordConfirmation.required'] = 'Password confirmation required'
    } else {
      delete errors['registration.passwordConfirmation.required']
      if (passwordConfirmation !== password) {
        errors['registration.passwordConfirmation.invalid'] = 'Passwords do not match'
      } else {
        delete errors['registration.passwordConfirmation.invalid']
      }
    }
    return errors
  }

  const onInputChange = (e) => {
    let updatedState = {...state, ...{[e.target.name]: e.target.value}}
    let errors = {}
    if (state.onSubmitted) {
      errors = validate(updatedState)
      updatedState = {...updatedState, ...{errors}}
    }
    setState(updatedState)
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    const errors = validate(state)
    delete errors['registration.email.user.name.taken']
    if (_.keys(errors).length) {
      setState({...state, ...{errors}})
      return
    }
    const updatedState = {
      ...state,
      ...{submitting: true},
      name: !_.isEmpty(state.name.trim()) ? state.name : getDefaultAccountName(state.email)
    }
    setState(updatedState)
    try {
      await createAccount(updatedState)
      mutateMe()
      navigate(`${basePath}/services`)
    } catch (e) {
      if (e.status === 409) {
        const {errors} = state
        errors['registration.email.user.name.taken'] = 'User is already registered for this email address. Please try again'
        setState({...updatedState, ...{errors, submitting: false}})
        return
      }
      setState({...updatedState, ...{submitting: false}})
      throw e
    }
  }

  const {name, email, first, last, password, passwordConfirmation, errors, submitting} = state
  const hasAccountError = _.keys(errors).some((k) => k.indexOf('registration.name') > -1)
  const hasFirstError = _.keys(errors).some((k) => k.indexOf('registration.first') > -1)
  const hasLastError = _.keys(errors).some((k) => k.indexOf('registration.last') > -1)
  const hasEmailError = _.keys(errors).some((k) => k.indexOf('registration.email') > -1)
  const hasPasswordError = _.keys(errors).some((k) => k.indexOf('registration.password') > -1)
  const hasPasswordConfirmationError = _.keys(errors).some((k) => k.indexOf('registration.passwordConfirmation') > -1)
  const hasErrors = hasAccountError || hasFirstError || hasLastError || hasEmailError || hasPasswordError || hasPasswordConfirmationError

  return (
    <Grid centered doubling columns={2}>
      <Grid.Column width='6'>
        <Header as='h1'>Create Account</Header>
        <Segment>
          {
            !!query.invitationId && (
              <Message success>
                Thanks for accepting your invitation to account: {query.accountName}. Please create a password and
                we'll send you on your way!
              </Message>)
          }
          <Form error={hasErrors} onSubmit={onSubmit}>
            {
              !query.invitationId && (
                <>
                  <Form.Field error={hasEmailError} required>
                    <label>Email</label>
                    <Form.Input placeholder='Enter email'
                                name='email'
                                value={email}
                                autoComplete='false'
                                onChange={onInputChange}/>
                  </Form.Field>
                  <Form.Field error={hasAccountError} required>
                    <label>Company/User Name</label>
                    <Form.Input placeholder='Enter company or user name'
                                name='name'
                                value={name || getDefaultAccountName(email)}
                                autoComplete='false'
                                onChange={onInputChange}/>
                  </Form.Field>
                </>
              )
            }
            <Form.Field error={hasFirstError} required>
              <label>First</label>
              <Form.Input placeholder='Enter first name'
                          name='first'
                          value={first}
                          autoComplete='false'
                          onChange={onInputChange}/>
            </Form.Field>
            <Form.Field error={hasLastError} required>
              <label>Last</label>
              <Form.Input placeholder='Enter last name'
                          name='last'
                          value={last}
                          autoComplete='false'
                          onChange={onInputChange}/>
            </Form.Field>
            <Form.Field error={hasPasswordError} required>
              <label>Password</label>
              <Form.Input placeholder='Enter password'
                          name='password'
                          value={password}
                          type='password'
                          autoComplete='false'
                          onChange={onInputChange}/>
            </Form.Field>
            <Form.Field error={hasPasswordConfirmationError} required>
              <label>Confirm Password</label>
              <Form.Input placeholder='Enter password confirmation'
                          name='passwordConfirmation'
                          value={passwordConfirmation}
                          type='password'
                          autoComplete='false'
                          onChange={onInputChange}/>
            </Form.Field>
            <Message error>
              <Message.Content>
                <List bulleted>
                  {
                    _.keys(errors).map((key) => (
                      <List.Item key={key}>{errors[key]}</List.Item>
                    ))
                  }
                </List>
              </Message.Content>
            </Message>
            <Button.Group fluid widths={2}>
              <Button primary className='create-account-submit' type='submit'
                      loading={submitting}>{!query.invitation ? 'Create Account' : 'Set Password'}</Button>
              <Button.Or/>
              <Button onClick={() => navigate('../login')}>Login</Button>
            </Button.Group>
          </Form>
        </Segment>
      </Grid.Column>
    </Grid>
  )
}

let CreateAccount = (props) => {
  const {handleError} = props
  props = {...props, createAccount: handleError(createAccount)}
  return (<_CreateAccount {...props}/>)
}

CreateAccount = withHelmet(CreateAccount, 'Create Account')
CreateAccount = withQueryParams(CreateAccount)
CreateAccount = withMeData(CreateAccount, 'Loading profile...')
CreateAccount = withErrorHandling(CreateAccount)
CreateAccount = memo(CreateAccount)

export {CreateAccount, _CreateAccount}

export default CreateAccount